import React, { useEffect, useLayoutEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Stack, Typography } from '@mui/material';

import { dismissInitialLoadingScreen } from '@/utils/dismissInitialLoadingScreen';

interface UnauthorizedPageProps {
  title?: string;
  message?: string;
  showRetryButton?: boolean;
}

const UnauthorizedPage: React.FC<UnauthorizedPageProps> = ({
  title,
  message,
  showRetryButton = true
}) => {
  const { logout } = useAuth0();
  const [searchParams] = useSearchParams();
  const errorType = searchParams.get('error');

  // Set default content based on error type
  const getErrorContent = () => {
    switch (errorType) {
      case 'ip-restricted':
        return {
          title: 'Access Restricted',
          message:
            "We couldn't verify your connection because your IP address is not on your organization's approved list.\n\nFor security reasons, only whitelisted IPs are permitted. If you believe this is a mistake or you need access, please contact your administrator or support team."
        };
      case 'access-denied':
        return {
          title: 'Access Denied',
          message:
            "You don't have permission to access this resource. Please contact your administrator if you believe this is an error."
        };
      default:
        return {
          title: title || 'Access Denied',
          message:
            message ||
            "You don't have permission to access this resource. Please contact your administrator if you believe this is an error."
        };
    }
  };

  const { title: errorTitle, message: errorMessage } = getErrorContent();

  useLayoutEffect(() => {
    dismissInitialLoadingScreen();
  }, []);

  // Log when this page loads to help with debugging
  useEffect(() => {
    console.log('UnauthorizedPage loaded with error type:', errorType);
  }, [errorType]);

  const handleLogout = () => {
    logout({
      returnTo: window.location.origin
    });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#FCFEFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
        zIndex: 9999
      }}
    >
      <Box
        sx={{
          maxWidth: 600,
          width: '100%',
          textAlign: 'center'
        }}
      >
        <Typography color="#0A1E28" fontSize="2rem" fontWeight="bold" mb={3}>
          {errorTitle}
        </Typography>

        <Typography
          color="#364955"
          fontSize="1.25rem"
          mb={4}
          sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}
        >
          {errorMessage}
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
        >
          <Button
            variant="outlined"
            onClick={handleLogout}
            sx={{ minWidth: 140, height: 48 }}
          >
            Sign Out
          </Button>

          <Button
            component={Link}
            to="/"
            variant="contained"
            sx={{ minWidth: 140, height: 48 }}
          >
            Go to Home Page
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default UnauthorizedPage;
